import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, Check, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { Logo } from "@/components/PublicNav";

const AGE_BANDS = [
  { id: "8-10", label: "8–10", note: "Tweens" },
  { id: "11-13", label: "11–13", note: "Early teens" },
  { id: "14-16", label: "14–16", note: "Mid teens" },
  { id: "17-19", label: "17–19", note: "Older teens" },
];
const STYLES = ["talkative", "private", "sensitive", "strong-willed", "easygoing", "not sure"];
const GOALS = ["Understand slang & trends", "Handle conflict calmly", "Notice mood changes", "Feel more connected", "Understand gaming & online life"];

export default function Onboarding() {
  const { user, setUser, refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [ageBand, setAgeBand] = useState("");
  const [nickname, setNickname] = useState("");
  const [styleTag, setStyleTag] = useState("");
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  if (user && user.onboarded) { nav("/app"); return null; }

  const toggleGoal = (g) => setGoals((cur) => cur.includes(g) ? cur.filter((x) => x !== g) : [...cur, g]);

  const finish = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/onboarding", { age_band: ageBand, nickname, style_tag: styleTag, goals });
      setUser(data.user);
      await refresh();
      toast.success("All set! Welcome to DecodeKid.");
      nav("/app");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const canNext = step === 1 ? true : step === 2 ? !!ageBand : true;

  return (
    <div className="min-h-screen bg-[#F4F6F4] py-8 px-4">
      <div className="max-w-xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <Logo />
          <span className="text-sm" style={{ color: "var(--text-muted)" }}>Step {step} of 4</span>
        </div>
        <div className="h-2 rounded-full mb-8" style={{ background: "var(--card-alt)" }}>
          <div className="h-2 rounded-full transition-all" style={{ width: `${step * 25}%`, background: "var(--accent-strong)" }} />
        </div>

        <div className="dk-card p-8 anim-in" data-testid="onboarding-wizard">
          {step === 1 && (
            <div className="space-y-4" data-testid="onboarding-step-1">
              <h1 className="font-serif text-[26px] font-semibold">Welcome to DecodeKid 🌱</h1>
              <p style={{ color: "var(--text-muted)" }}>DecodeKid helps you understand your child's world and find calm, caring words to respond. A few things to know:</p>
              <ul className="space-y-2 text-sm">
                <li className="flex gap-2"><Check size={18} style={{ color: "var(--success)" }} className="shrink-0" /> Your free 3-day trial has started — every tool, no card needed.</li>
                <li className="flex gap-2"><Check size={18} style={{ color: "var(--success)" }} className="shrink-0" /> It's a gentle guide, not therapy or a monitoring tool.</li>
                <li className="flex gap-2"><Lock size={18} style={{ color: "var(--sage)" }} className="shrink-0" /> Everything you enter about your child is encrypted and visible only to you.</li>
              </ul>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h1 className="font-serif text-[26px] font-semibold">Your first child</h1>
              <p style={{ color: "var(--text-muted)" }}>No real names or photos needed. Choose an age band:</p>
              <div className="grid grid-cols-2 gap-3">
                {AGE_BANDS.map((b) => (
                  <button key={b.id} onClick={() => setAgeBand(b.id)} data-testid={`age-band-${b.id}`}
                    className={`dk-card p-4 text-left transition-all ${ageBand === b.id ? "ring-2" : ""}`} style={ageBand === b.id ? { borderColor: "var(--accent)", boxShadow: "0 0 0 2px var(--accent)" } : {}}>
                    <div className="font-serif text-[22px]">{b.label}</div>
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>{b.note}</div>
                  </button>
                ))}
              </div>
              <div>
                <label className="text-sm font-medium">Nickname or initial (optional)</label>
                <input className="dk-input mt-1" value={nickname} maxLength={40} onChange={(e) => setNickname(e.target.value)} placeholder="e.g. “J” or “Bug”" data-testid="onboarding-nickname" />
              </div>
              <div>
                <label className="text-sm font-medium">How would you describe them? (optional)</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {STYLES.map((s) => (
                    <button key={s} onClick={() => setStyleTag(styleTag === s ? "" : s)} className={`badge-soft ${styleTag === s ? "" : "opacity-70"}`} style={styleTag === s ? { background: "var(--sage)", color: "#fff" } : {}} data-testid={`style-${s}`}>{s}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h1 className="font-serif text-[26px] font-semibold">What brings you here?</h1>
              <p style={{ color: "var(--text-muted)" }}>Pick any that fit — we'll tailor tips to you.</p>
              <div className="space-y-2">
                {GOALS.map((g) => (
                  <button key={g} onClick={() => toggleGoal(g)} data-testid={`goal-${g}`}
                    className={`w-full dk-card p-4 text-left flex items-center justify-between ${goals.includes(g) ? "ring-2" : ""}`} style={goals.includes(g) ? { borderColor: "var(--accent)" } : {}}>
                    <span>{g}</span>
                    {goals.includes(g) && <Check size={18} style={{ color: "var(--accent-strong)" }} />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4 text-center">
              <span className="grid place-items-center w-14 h-14 rounded-full mx-auto" style={{ background: "var(--sage-tint)" }}><Lock style={{ color: "var(--sage)" }} /></span>
              <h1 className="font-serif text-[26px] font-semibold">Your privacy, promised</h1>
              <p style={{ color: "var(--text-muted)" }}>Everything you just entered is encrypted with a key only your account can use. No DecodeKid staff can ever read it. No device tracking, ever. You're always in control.</p>
              <div className="rounded-xl p-4 text-sm text-left" style={{ background: "var(--card-alt)" }}>
                <b>Ready:</b> a {ageBand} child{nickname && ` (${nickname})`}{styleTag && `, ${styleTag}`}. {goals.length > 0 && `Focus: ${goals.length} goal${goals.length > 1 ? "s" : ""}.`}
              </div>
            </div>
          )}

          <div className="flex justify-between mt-8">
            <button className="btn btn-ghost" onClick={() => step === 1 ? nav("/app") : setStep(step - 1)} data-testid="onboarding-back-btn">
              <ArrowLeft size={16} /> {step === 1 ? "Skip" : "Back"}
            </button>
            {step < 4 ? (
              <button className="btn btn-primary" disabled={!canNext} onClick={() => setStep(step + 1)} data-testid="onboarding-next-btn">Next <ArrowRight size={16} /></button>
            ) : (
              <button className="btn btn-primary" disabled={loading} onClick={finish} data-testid="onboarding-finish-btn">{loading && <Loader2 size={16} className="animate-spin" />} Enter DecodeKid</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
